"use client";

import React from "react";
import {
  Button,
  BUTTON_SIZES,
  BUTTON_VARIANTS,
  type ButtonSize,
  type ButtonVariant
} from "../../atoms/button/button";
import { Link, LINK_ACTIONS, safeHref, type LinkAction } from "../../atoms/link/link";
import { PriceBlock } from "../../molecules/price-block/price-block";
import { Tag } from "../../atoms/tag/tag";
import {
  TEXT_BODY_ELEMENTS,
  TEXT_BODY_VARIANTS,
  TEXT_HEADING_VARIANTS,
  TEXT_LEVELS,
  Text,
  safeElement,
  safeVariant,
  type TextBodyElement,
  type TextBodyVariant,
  type TextHeadingVariant,
  type TextLevel
} from "../../atoms/text/text";
import { LINK_POSITION_BODY } from "../../services/analytics";
import { ctaAriaLabel } from "../../services/cta-aria-label";
import { isSafeImageSrc } from "../../services/safe-image-src";
import {
  AA_NORMAL,
  assetUrl,
  content,
  list,
  paint,
  text,
  titleContrast
} from "./grow-card-carousel.fields";
import styles from "./grow-card.module.css";
import { withSafeRender } from "../../atoms/safe-render";

/* One genre-pack tile: `grown` and `ungrown` differ in WHICH BLOCKS RENDER,
   not only in scale. Not a control — click-to-grow belongs to the shell.
   "use client" is for the React.memo below AND the useRef in the component. */

/** Which blocks render. See the component note above. */
export type GrowCardSize = "grown" | "ungrown";

/**
 * A Storyblok Asset field, which publishes an object where a plain URL field
 * publishes a string. Every asset prop below admits BOTH, because both arrive
 * from the feeds and `assetUrl()` reads either.
 *
 * ⚠ Declared on the props rather than left to a cast at the call site
 * (WAT-12200). Typed `string` alone, a direct-blok caller has to write
 * `as unknown as string` — and once that cast is in place a later
 * `card.image_desktop_url` read type-checks and renders `src="[object Object]"`.
 * The type has to admit what the reader already handles.
 */
export type StoryblokAsset = {
  filename?: string;
  alt?: string;
};

/**
 * A repeatable's entries, or the single entry `lib/helper/sb-fixer.ts`
 * collapses a one-element array to. `list()` coerces the bare entry back;
 * the type says so rather than making every caller cast.
 */
export type GrowCardRepeatable<T> = T[] | T;

/** A repeatable logo — a channel mark or a streaming-app tile. */
export interface GrowCardLogo {
  /** Image URL, or the Asset object. An entry without one is dropped. */
  logo_url?: string | StoryblokAsset;
  /**
   * Alternative text. Blank marks the logo decorative, which is right wherever
   * the surrounding copy already names the channel or app — and is the
   * standing call for a COMPOSITE image, whose several brands no single alt
   * can enumerate without authored copy that tracks the artwork (WAT-12079).
   */
  logo_alt?: string;
}

/** One "What you get" bullet. */
export interface GrowCardBenefit {
  /** Decorative mark beside the wording, or its Asset object. Absent renders no mark and no gap. */
  icon_url?: string | StoryblokAsset;
  /** The bullet's wording. An entry without it is dropped — a lone icon announces nothing. */
  benefit_content?: string;
  /** The wording's Text variant, from the body steps. Defaults to `body-3`. */
  benefit_variant?: TextBodyVariant;
  /** The wording's element: p, span or div. Defaults to p. */
  benefit_element?: TextBodyElement;
}

/**
 * One authored card. Flat by design, and this shape IS the CMS contract: every
 * field below is a Storyblok field name, and the docs page is the schema those
 * names are authored against (WAT-12200).
 *
 * The card blok reaches here UNCHANGED. `growCardCarouselAdapter` passes it
 * through and adds only what it alone can — the resolved `svods`, the Dynamic
 * Pricing copy, and a string coercion on `id` and `accent` — so
 * a field added here and to the docs needs no edit in the adapter to arrive.
 * Nothing renames anything, in either direction.
 *
 * Every content and label field is optional, and its absence suppresses the
 * element it feeds along with that element's wrapper and spacing.
 *
 * ⚠ The declared types are the IDEAL shape, not a promise about what arrives.
 * A CMS response is `unknown`: a switch can arrive as the string `"false"`, an
 * asset field as `{ filename }`, a one-entry repeatable as a bare object, and
 * a closed-set field as anything at all. Every reader below coerces rather
 * than trusting the declaration, and nothing here throws.
 */
export interface GrowCardData {
  /** Stable identity: the React key, the tab value, and the bloom layers' key. */
  id: string;
  /** Pack name. */
  title_content?: string;
  /** Overrides the frame's own title Text variant, from the HEADING steps only.
   *  Anything else is ignored, published on Text or not. */
  title_variant?: TextHeadingVariant;
  /** The title's element: h1–h6, or p for a name outside the page outline. Defaults to h3. */
  title_level?: TextLevel;
  /** The tray's wording for this card. Read by the carousel shell, not by the card. */
  tab_label?: string;
  /** The channel-count line under the title, e.g. "View all 60+ channels". */
  subheading_content?: string;
  /** The subheading's Text variant, from the body steps. Defaults to `body-3`.
   *  Plain-text subheading only — a linked one takes the link's own size. */
  subheading_variant?: TextBodyVariant;
  /** The subheading's element: p, span or div. Defaults to span. Plain-text only. */
  subheading_element?: TextBodyElement;
  /** Given, the subheading is a link to it; absent, it renders as plain text. */
  subheading_url?: string;
  /**
   * What the linked subheading ("View all N+ channels") does: `redirect` (the
   * default) navigates to `subheading_url`; `iframe_modal` opens it in the page's
   * iframe modal instead, embedding that url while it stays the no-JS fallback.
   * Passed straight to the Link atom's `linkAction`; ignored on a plain-text
   * subheading. `iframe_modal` needs an `IframeModal` on the page. CI-configurable.
   */
  subheading_action?: LinkAction;
  /** Hex colour or palette step driving the card's gradient. Anything else is refused. */
  accent: string;
  /**
   * The darker step of the same family. No shipped rule reads it today.
   *
   * No frame binds a second accent stop, and AC7 names a card's accent
   * colours plural — retiring it is an AC change (WAT-11671 open question 3),
   * not a refactor.
   */
  accent_deep?: string;
  /** The pack's own 50 step — the title is tinted per pack, not shared. */
  title_color?: string;
  /** Pack artwork, rendered at the 1280 tier and up. Either shape — see `StoryblokAsset`. */
  image_desktop_url?: string | StoryblokAsset;
  /** Blank marks the artwork decorative, which is what the frame draws. */
  image_alt?: string;
  /**
   * Channel marks, rendered in BOTH sizes as one row that may wrap onto further
   * lines, suppressed entirely when empty. An entry may be a single mark OR a
   * composite image holding several — see `GrowCardChannelLogoLayout`.
   */
  channel_logos?: GrowCardRepeatable<GrowCardLogo>;
  /** Streaming-app tiles, above the channel marks. Both sizes. */
  svods?: GrowCardRepeatable<GrowCardLogo>;
  /** Previous price, struck through. Arrives already resolved. GROWN only. */
  strikethrough_price?: string;
  /**
   * The price, e.g. "$49". Without it the whole price block is suppressed. GROWN only.
   * With `price_cents` empty, a whole price ("$49.99/mo.") splits at its decimal point.
   */
  price_content?: string;
  /** Cents portion, e.g. ".99/mo". GROWN only. */
  price_cents?: string;
  /** Suffix under the price, e.g. "+ taxes & fees". GROWN only. */
  price_frequency?: string;
  /** Legal copy under the price, e.g. "See details". GROWN only. */
  legal_content?: string;
  /** The legal copy's Text variant, from the body steps. Defaults to `label`. */
  legal_variant?: TextBodyVariant;
  /** The legal copy's element: p, span or div. Defaults to p. */
  legal_element?: TextBodyElement;
  /** Given, the legal copy becomes a link to it. GROWN only. */
  legal_url?: string;
  /**
   * Whether the call to action renders at all. Its WORDING is fixed at
   * `CTA_LABEL` for MVP1 (AC11), so an authored value is ignored. GROWN only.
   */
  cta_label?: string;
  /** Given, the call to action navigates as a link; absent, it renders a button. */
  cta_url?: string;
  /** The call to action's accessible name, used as authored when set; CI owns
   *  its wording. Blank reads as unset, so the visible wording names it. GROWN only. */
  cta_aria_label?: string;
  /** Call-to-action appearance. Defaults to the frame's primary. Declared as
   *  AUTHORED — `ctaVariantIsPublished` below is the reader (WAT-12200). */
  cta_variant?: ButtonVariant | (string & {});
  /** Call-to-action size. Defaults to the frame's small. Declared as AUTHORED
   *  — `ctaSizeIsPublished` below is the reader (WAT-12200). */
  cta_size?: ButtonSize | (string & {});
  /** Heading over the bullets, e.g. "What you get". GROWN only. */
  benefits_heading_content?: string;
  /** The bullets heading's element: h1–h6, or p (the default) for a line outside the outline. */
  benefits_heading_level?: TextLevel;
  /** The bullets heading's Text variant, from the heading steps. Default `body-3`. */
  benefits_heading_variant?: TextHeadingVariant;
  /** The bullets, three to a row at desktop. GROWN only. */
  benefits?: GrowCardRepeatable<GrowCardBenefit>;
  /**
   * The compare affordance's wording, e.g. "Compare Packs". Its PRESENCE
   * gates the affordance in either form — the tonal link `compare_url` makes
   * of it, or the plain run painted the same way with no destination
   * authored. GROWN only.
   */
  compare_label?: string;
  /** Given, the compare affordance is a tonal link to it; absent, or refused by
   *  `safeHref`, it stays PLAIN TEXT — deliberately unlike `cta_url`, where AC11
   *  wants a button either way. design-conflicts/grow-card-carousel.mdx. */
  compare_url?: string;
  /** The compare link's accessible name, used as authored when set; CI owns
   *  its wording. Blank reads as unset, so the visible wording names it. Reaches
   *  only the LINKED form — the plain run has no accessible name to set. GROWN only. */
  compare_aria_label?: string;
  /** The compare link's appearance. Defaults to the frame's tonal. Declared as
   *  AUTHORED — `compareVariantIsPublished` below is the reader (WAT-12516).
   *  Reaches the LINKED form only, like `compare_aria_label`. GROWN only. */
  compare_variant?: ButtonVariant | (string & {});
  /** The compare link's size. Defaults to the frame's small. Declared as
   *  AUTHORED — `compareSizeIsPublished` below is the reader (WAT-12516).
   *  Reaches the LINKED form only. GROWN only. */
  compare_size?: ButtonSize | (string & {});
  /**
   * Offer-tag copy, e.g. "Special offer!", top-right of the card at the 1280
   * tier and up in both sizes. Absent renders no tag, no band and no spacing.
   */
  tag_label?: string;
}

/* The single runtime source: the unions, the guards and the stories' `options`
   all derive from these, so no hand-written copy can drift from them. */

/* How the marks fill their share of the channel-logo row. The real CSS keywords.
   Not JSDoc, here or below: Storybook's docgen reads a documented export as a
   component and adds keys to the object. */
export const CHANNEL_LOGO_FITS = {
  contain: true,
  cover: true,
  fill: true,
  none: true,
  "scale-down": true
} as const;

/* Author-facing alignment, mapped to the flex value the row actually takes. */
export const CHANNEL_LOGO_ALIGNS = {
  between: "space-between",
  center: "center",
  end: "flex-end",
  start: "flex-start"
} as const;

export type GrowCardChannelLogoFit = keyof typeof CHANNEL_LOGO_FITS;

/** Where the row sits when the marks do not fill the card's width. */
export type GrowCardChannelLogoAlign = keyof typeof CHANNEL_LOGO_ALIGNS;

/** A height as AUTHORED: a Storyblok number field publishes the string "56" as
 *  readily as 56, and `logoHeight()` already reads both (WAT-12200). */
export type StoryblokPixels = number | string;

/**
 * The channel-logo row's sizing. Uniform across the rail, so it is authored ONCE
 * on the section and forwarded to every card.
 *
 * ⚠ The row is WIDTH-DRIVEN: it sits in the frame's own 194px box and the marks
 * wrap inside it, so how many land on a line is DERIVED and never authored.
 *
 * Every member is declared as AUTHORED, not as accepted: the readers below take
 * `unknown` and refuse anything off-contract, so a narrower declaration would
 * only force a cast at the boundary and lie about what arrives (WAT-12200).
 */
export interface GrowCardChannelLogoLayout {
  /** The tallest the marks draw, in the design's own pixels, e.g. 56. Base tier. */
  channel_logo_max_height?: StoryblokPixels;
  /** The same, from 735 up. Unset falls back to the base value. */
  channel_logo_max_height_tablet?: StoryblokPixels;
  /** The same, from 1280 up. Unset falls back to the tablet value, then the base one. */
  channel_logo_max_height_desktop?: StoryblokPixels;
  /** The same, from 1732 up. Unset falls back down the same chain. */
  channel_logo_max_height_wide?: StoryblokPixels;
  /**
   * How each mark fills its share of the row. Defaults to the frame's `contain`,
   * which letterboxes rather than crops. One value at every tier.
   */
  channel_logo_object_fit?: GrowCardChannelLogoFit | (string & {});
  /** Where the row sits when the marks leave width over. Defaults to `start`, at every tier. */
  channel_logo_align?: GrowCardChannelLogoAlign | (string & {});
}

export interface GrowCardProps extends GrowCardChannelLogoLayout {
  /** The authored card. */
  card: GrowCardData;
  /** Which blocks render. Defaults to `ungrown`. */
  size?: GrowCardSize;
  /** Marks the card in the grow position for assistive tech. */
  active?: boolean;
  /* Do not re-add `reflects` (WAT-12516). The authored artwork already
     carries its own reflection; a code-drawn one would paint a second.
     design-conflicts/grow-card-carousel.mdx. */
  /**
   * Where on the page the card sits, for reporting. Rendered as
   * `data-linkposition` on the subheading and legal LINKS, falling back to
   * `body` when unauthored (WAT-12516) — the prop itself reaches NEITHER
   * Button (WAT-11941); the call to action and the compare link each state
   * their own `body` placement instead.
   */
  linkPosition?: string;
}

/* Every reader below takes `unknown`: what arrives is a CMS response. Nothing
   here throws — a card that throws takes the whole rail with it. `list()`,
   `text()` and `assetUrl()` are the shared ones, in grow-card-carousel.fields
   so the section and the card cannot read a field two different ways. */

/**
 * Whether the field was authored at all. An unset Storyblok field arrives as
 * "", null or undefined; zero and NaN count as authored and so are refused
 * and reported rather than treated as absent.
 */
function isAuthored(value: unknown): boolean {
  return value !== null && value !== undefined && value !== "";
}

/* ONE custom property per tier, not one value: an inline style cannot answer a
   media query, so all four heights go out side by side and the stylesheet
   re-points its own --gcc-chl-h at the right one. None of them may be
   `@property`-registered — see the reason at .channelLogos in the stylesheet. */

/**
 * The root font size every px figure in the token layer was chosen against.
 * `tokens/breakpoints.spec.ts` goes RED if any stylesheet pins
 * `html { font-size }`, so this is a fixed reference rather than a guess.
 *
 * ⚠ "Goes red" means `yarn test` — jest, run by hand. It is NOT a build
 * gate: `yarn build` runs `next build` plus `tsc --project tsconfig.server.json`
 * and no jest at all, and the one pull-request workflow in `.github/workflows/`
 * that mentions tests only checks that a `.spec.ts(x)` FILE EXISTS beside each
 * changed source file — it never runs one. Nothing here fails a CI gate today.
 */
const ROOT_FONT_PX = 16;

/* "$49.99/mo. for first 1 month*" → "$49" | ".99/mo. for first 1 month*". */
const WHOLE_PRICE = /^(.*?\d)(\.\d.*)$/;

/* AC11, MVP1: one wording for every pack so the button cannot wrap to two
   lines. `cta_label`'s PRESENCE still gates the button; its value is ignored. */
export const CTA_LABEL = "Add to cart";

/**
 * A row height as `rem`, or undefined so the stylesheet's own tier value
 * stands. Authored in the design's PIXELS and emitted in `rem`.
 */
function logoHeight(value: unknown): string | undefined {
  const px = typeof value === "number" ? value : Number(text(value));

  if (!Number.isFinite(px) || px <= 0) {
    return undefined;
  }

  return `${px / ROOT_FONT_PX}rem`;
}

/** The keyword, or undefined so the stylesheet's own `contain` stands. */
function logoFit(value: unknown): GrowCardChannelLogoFit | undefined {
  const raw = text(value);

  return Object.prototype.hasOwnProperty.call(CHANNEL_LOGO_FITS, raw) ?
    (raw as GrowCardChannelLogoFit) :
    undefined;
}

/** The row's flex alignment, or undefined so the stylesheet's own `flex-start` stands. */
function logoAlign(value: unknown): string | undefined {
  const raw = text(value);

  return Object.prototype.hasOwnProperty.call(CHANNEL_LOGO_ALIGNS, raw) ?
    CHANNEL_LOGO_ALIGNS[raw as GrowCardChannelLogoAlign] :
    undefined;
}

/**
 * `[field "value", what it accepts]` for each `[field, authored, published,
 * accepts]` row that was authored and refused. Shared by the call to action and
 * the compare link, which take the same two Button fields and refuse them
 * identically — one wording per control, built the one way.
 */
function selectRefusedButtonFields(
  checked: [string, string, boolean, string][]
): [string, string][] {
  return checked
    .filter(([, authored, published]) => authored !== "" && !published)
    .map(([field, authored, , accepts]): [string, string] => [
      `${field} "${authored}"`,
      accepts
    ]);
}

/**
 * The layout fields whose authored value was refused, each with what it does
 * accept. An unauthored field is not in the list — see `isAuthored`.
 */
function selectRefusedLogoLayout(
  layout: GrowCardChannelLogoLayout
): [string, string][] {
  const height = "a height in the design's own pixels, e.g. 56";
  const checked: [string, unknown, unknown, string][] = [
    [
      "channel_logo_align",
      layout.channel_logo_align,
      logoAlign(layout.channel_logo_align),
      "start, center, end or between"
    ],
    [
      "channel_logo_max_height",
      layout.channel_logo_max_height,
      logoHeight(layout.channel_logo_max_height),
      height
    ],
    [
      "channel_logo_max_height_desktop",
      layout.channel_logo_max_height_desktop,
      logoHeight(layout.channel_logo_max_height_desktop),
      height
    ],
    [
      "channel_logo_max_height_tablet",
      layout.channel_logo_max_height_tablet,
      logoHeight(layout.channel_logo_max_height_tablet),
      height
    ],
    [
      "channel_logo_max_height_wide",
      layout.channel_logo_max_height_wide,
      logoHeight(layout.channel_logo_max_height_wide),
      height
    ],
    [
      "channel_logo_object_fit",
      layout.channel_logo_object_fit,
      logoFit(layout.channel_logo_object_fit),
      "contain, cover, fill, none or scale-down"
    ]
  ];

  return checked
    .filter(([, authored, resolved]) => isAuthored(authored) && resolved === undefined)
    .map(([field, , , accepts]): [string, string] => [field, accepts]);
}

/**
 * An asset field's URL, or "" for a value no `<img src>` may take. ONE GATE
 * PER PRODUCER and never at the `<img>` itself — see services/safe-image-src.ts,
 * "ONE POLICY FOR EVERY IMAGE SINK" — so a tag added below inherits it. The
 * answer for a refused value is "", the same answer an unauthored asset gives,
 * so each sink's existing `!== ""` check takes the element and its wrapper with
 * it rather than painting `<img src="">`.
 *
 * WAT-12200: the component is the CMS boundary now, so the policy the sibling
 * organisms apply — plan-card.tsx, the `isSafeImageSrc` filter in
 * `renderableLogos()` and `PlanCard()`'s own `imageSrc` gate — has to be
 * applied HERE. `data:` survives only for an image media type and a relative
 * URL resolves against the site's own origin, so every shipped asset path
 * still paints.
 */
function safeAssetUrl(value: unknown): string {
  const url = assetUrl(value);

  return isSafeImageSrc(url) ? url : "";
}

/**
 * A logo after normalisation: both fields a string, never `undefined`. That is
 * what keeps the rendered `alt` PRESENT when it is empty — React omits the
 * attribute entirely for `undefined`, and an `<img>` carrying no `alt` at all
 * is announced by its filename, worse than either a poor alt or a deliberately
 * decorative one. Spelled out rather than `Required<GrowCardLogo>`, which
 * would make a field added to that interface later mandatory here too.
 */
type RenderableLogo = {
  logo_alt: string;
  logo_url: string;
};

/**
 * Logos with an image, in authored order. An entry without a `logo_url` paints
 * nothing. `logo_alt` goes through `content()`, not `text()`, so an unresolved
 * `[<TOKEN>]` doesn't get read out as the image's accessible name (AC8).
 * `logo_url` goes through `safeAssetUrl()`: the field is authored as a
 * Storyblok Asset on some feeds and as a plain URL string on others, and an
 * entry whose URL no `<img src>` may take is dropped with the ones carrying
 * no URL at all — a logo tile rendering empty beside its neighbours is worse
 * than one absent row.
 */
function selectRenderableLogos(
  value: unknown,
  field: string,
  unresolved: string[]
): RenderableLogo[] {
  return list<GrowCardLogo>(value)
    .map((logo, index) => ({
      logo_alt: content(logo?.logo_alt, `${field}[${index}].logo_alt`, unresolved),
      logo_url: safeAssetUrl(logo?.logo_url)
    }))
    .filter(logo => logo.logo_url !== "");
}

/** A benefit after normalisation, the counterpart of `RenderableLogo` above:
 *  `icon_url` is the resolved URL string, never the Asset object the prop
 *  also admits, so the `<img src>` below takes it directly. */
type RenderableBenefit = {
  icon_url: string;
  content: string;
  variant: TextBodyVariant;
  element: TextBodyElement;
};

/** Bullets with wording, in authored order. The icon stays optional. */
function selectRenderableBenefits(
  value: unknown,
  unresolved: string[]
): RenderableBenefit[] {
  return list<GrowCardBenefit>(value)
    .map((benefit, index) => ({
      icon_url: safeAssetUrl(benefit?.icon_url),
      content: content(benefit?.benefit_content, `benefits[${index}].benefit_content`, unresolved),
      variant: safeVariant(text(benefit?.benefit_variant), "body-3", TEXT_BODY_VARIANTS),
      element: safeElement(text(benefit?.benefit_element), "p", TEXT_BODY_ELEMENTS)
    }))
    .filter(benefit => benefit.content !== "");
}

/* What a refusal actually costs, per value — the card publishes all three but
   only two are read under `.card`; a new paint field added to `paints` below
   fails to compile until named here. */
type PaintField = "accent" | "accent_deep" | "title_color";

const REFUSAL_COST: Record<PaintField, string> = {
  accent:
    "the card's gradient inherits the section's accent — the grown card's — and re-tints as the selection moves",
  accent_deep: "nothing on the card changes — no shipped rule reads --gcc-accent-deep any more",
  title_color: "the title keeps the stylesheet's own colour"
};

type DevelopmentWarningInput = {
  id: string;
  unresolved: string[];
  refusedPaints: PaintField[];
  refusedLogoLayout: [string, string][];
  /** `[field "value", what the field accepts]`, only where the CTA renders. */
  refusedCta: [string, string][];
  /** The same, only where the compare LINK renders — on the plain run the two
   *  fields reach no DOM, so a refusal there would name nothing on screen. */
  refusedCompare: [string, string][];
  /** The authored `compare_url` the card would not link to, or "" for none —
   *  only where the compare wording renders. NEW with WAT-12516: there is no
   *  older url-refusal diagnostic in this file for it to follow. */
  refusedCompareUrl: string;
  titleVariant: string;
  titleAA?: number;
  /** Whether `titleAA` includes the accent-wash leg, or is the fill leg alone
   *  — the two measure different backdrops, so they get different wording. */
  titleAAAgainstWash: boolean;
  droppedByMissingPrice: string[];
  /** Diagnostics already emitted by this instance, keyed by message. */
  seen: Set<string>;
};

/**
 * Development-only. Each branch reports a card that renders a perfectly good
 * layout with something quietly missing from it, which is the only failure mode
 * a picture does not show.
 */
function warnInDevelopment({
  id,
  unresolved,
  refusedPaints,
  refusedLogoLayout,
  refusedCta,
  refusedCompare,
  refusedCompareUrl,
  titleVariant,
  titleAA,
  titleAAAgainstWash,
  droppedByMissingPrice,
  seen
}: DevelopmentWarningInput) {
  const card = id ? `card "${id}"` : "an unidentified card";
  const once = (message: string) => {
    if (seen.has(message)) {
      return;
    }
    seen.add(message);
    console.warn(message);
  };

  if (unresolved.length > 0) {
    once(
      `[GrowCard] ${card} suppressed ${unresolved.join(", ")} because the ` +
        "value still holds an unresolved [<TOKEN>], which must never reach a " +
        "customer. Dynamic text is resolved server-side before it gets here, so " +
        "this is an authoring or a resolution gap rather than a card problem."
    );
  }

  /* FORMAT only — `paint()` refuses nothing else. A well-formed accent the
     rail's focus ring cannot be seen over is refused on the server and reports
     itself there, in its own wording (ring-legibility.server.ts). */
  for (const field of refusedPaints) {
    once(
      `[GrowCard] ${card} refused ${field} because the value is not a colour, ` +
        `so ${REFUSAL_COST[field]}. Author a hex colour (#680dab) or a ` +
        "PUBLISHED palette step (amethyst-600) — a step the token layer does " +
        "not publish resolves to nothing, and anything else can pull arbitrary " +
        "CSS into the card."
    );
  }

  for (const [field, accepts] of refusedLogoLayout) {
    once(
      `[GrowCard] ${card} refused ${field}, so the channel-logo row keeps the ` +
        `frame's own sizing at that tier. Author ${accepts}.`
    );
  }

  for (const [refusal, accepts] of refusedCta) {
    once(
      `[GrowCard] ${card} has an unknown ${refusal}, so the call to action ` +
        `renders as the frame draws it. Pass a published Button ${accepts}.`
    );
  }

  for (const [refusal, accepts] of refusedCompare) {
    once(
      `[GrowCard] ${card} has an unknown ${refusal}, so the compare link ` +
        `renders as the frame draws it. Pass a published Button ${accepts}.`
    );
  }

  /* The only refusal with NO visual tell: the run and the button are painted
     the same, so a preview of the card looks right either way. */
  if (refusedCompareUrl !== "") {
    once(
      `[GrowCard] ${card} would not link compare_url "${refusedCompareUrl}", so ` +
        "the compare wording renders as PLAIN TEXT that looks like the link and " +
        "cannot be clicked. Author a path starting with \"/\" or a full https:// " +
        "address, in plain ASCII — an accented character anywhere in the value " +
        "is refused too (atoms/link/link.href.ts)."
    );
  }

  /* "refused", not "unknown": a pack name takes a HEADING step, so
     `number-strikethrough` and the `text-link-*` steps are refused here even
     though Text publishes them — calling those unknown sends the author
     looking for a typo they did not make (WAT-12200). */
  if (
    titleVariant !== "" &&
    !Object.prototype.hasOwnProperty.call(TEXT_HEADING_VARIANTS, titleVariant)
  ) {
    once(
      `[GrowCard] ${card} refused title_variant "${titleVariant}", so the title ` +
        "renders at the size the frame draws. A pack name takes a heading step: " +
        `author one of ${Object.keys(TEXT_HEADING_VARIANTS).join(", ")}; or clear it.`
    );
  }

  if (titleAA !== undefined && titleAA < AA_NORMAL) {
    /* TWO sentences for the two legs titleContrast() takes. Naming the accent
       where no accent went into the figure sends the author to a field that
       cannot fix it — and on a card that paints no accent of its own, it is
       the section's, not this card's, to change (WAT-12200). */
    const shortfall =
      `under the ${AA_NORMAL}:1 WCAG AA needs for normal text. The title still ` +
      "renders as authored — nothing is refused";

    once(
      titleAAAgainstWash ?
        `[GrowCard] ${card} pairs title_color with accent at ${titleAA.toFixed(2)}:1, ` +
          `${shortfall} — but it is hard to read against the wash. Lighten ` +
          "title_color or deepen accent." :
        `[GrowCard] ${card} puts title_color on the card's own fill at ` +
          `${titleAA.toFixed(2)}:1, ${shortfall} — but it is hard to read on the ` +
          "card. No accent is in this figure because this card publishes none: " +
          "accent is unauthored, is not a colour, or was refused, so the wash " +
          "reading is dropped and only --slate-950 is measured. Lighten title_color."
    );
  }

  if (droppedByMissingPrice.length > 0) {
    once(
      `[GrowCard] ${card} authored ${droppedByMissingPrice.join(", ")} without ` +
        "price_content. Those runs sit inside the price block, so they are " +
        "dropped with it — a struck price and legal copy with no live price " +
        "beside them is not a state the design publishes."
    );
  }
}

function GrowCardBase({
  card,
  size = "ungrown",
  active = false,
  linkPosition,
  /* The six channel-logo sizing fields, kept together so the shell can forward
     them with one spread. ⚠ A prop added to GrowCardProps and NOT destructured
     above lands here and is SILENTLY DROPPED: the style object below publishes
     six named keys, never the rest object. Name every new prop above. */
  ...logoLayout
}: GrowCardProps) {
  /* Latches each diagnostic to once per instance. ⚠ Ahead of the `!card`
     guard because a hook must not sit behind an early return. */
  const warnedRef = React.useRef<Set<string>>(new Set());

  if (!card) {
    return null;
  }

  const grown = size === "grown";
  const unresolved: string[] = [];

  const title = content(card.title_content, "title_content", unresolved);
  const subheading = content(card.subheading_content, "subheading_content", unresolved);
  const tagLabel = content(card.tag_label, "tag_label", unresolved);
  /* GROWN only: the ungrown frames draw no price, no legal copy, no call to
     action and no compare affordance at any tier, so those fields have to leave
     the TREE (AC35). Gating the READ rather than the JSX is what takes each
     wrapper and its spacing with it. */
  const strikethroughPrice = grown ? content(card.strikethrough_price, "strikethrough_price", unresolved) : "";
  const priceContent = grown ? content(card.price_content, "price_content", unresolved) : "";
  const priceCents = grown ? content(card.price_cents, "price_cents", unresolved) : "";
  /* Dynamic pricing resolves the whole price into price_content; split it at the
     first decimal point so the cents and suffix take the cents run's type. */
  const wholePrice = priceCents === "" ? WHOLE_PRICE.exec(priceContent) : null;
  const dollars = wholePrice ? wholePrice[1] : priceContent;
  const cents = wholePrice ? wholePrice[2] : priceCents;
  const frequency = grown ? content(card.price_frequency, "price_frequency", unresolved) : "";
  const legalContent = grown ? content(card.legal_content, "legal_content", unresolved) : "";
  const ctaLabel = grown ? content(card.cta_label, "cta_label", unresolved) : "";
  const ctaName = grown ? ctaAriaLabel(content(card.cta_aria_label, "cta_aria_label", unresolved)) : undefined;
  const benefitsHeading = content(
    card.benefits_heading_content,
    "benefits_heading_content",
    unresolved
  );
  const compareLabel = grown ? content(card.compare_label, "compare_label", unresolved) : "";

  /* These two go to `Link`, which sanitises the scheme itself. The CTA's url
     is sanitised BELOW, through that same `safeHref`, precisely because
     `Button` renders what it is handed verbatim as its `href` — and since
     WAT-12516 that sanitised value is its `data-linkdestination` too. */
  const subheadingUrl = text(card.subheading_url);
  /* Checked against the Link atom's own set rather than cast (AC2 style, like
     the CTA size/variant below) — an off-set value leaves the subheading a
     plain redirect. Passed to the Link's `linkAction`, which turns `iframe_modal`
     into the trigger attribute; the destination stays `subheading_url`. */
  const authoredSubheadingAction = text(card.subheading_action);
  const subheadingAction = Object.prototype.hasOwnProperty.call(LINK_ACTIONS, authoredSubheadingAction) ?
    (authoredSubheadingAction as LinkAction) :
    undefined;
  const legalUrl = text(card.legal_url);
  /* An href is CMS content, so `javascript:` and the off-site `//host/x` both
     arrive as ordinary strings — sanitised through the LINK ATOM'S OWN guard.
     ⚠ Only an AUTHORED value is sanitised: safeHref answers "#" for what it
     refuses, which would turn the unauthored case into a link to nowhere
     where AC11 wants a plain button. */
  const authoredCtaUrl = text(card.cta_url);
  const ctaUrl = authoredCtaUrl === "" ? "" : safeHref(authoredCtaUrl);
  /* Both reporting props or neither, on the same test that content-card-carousel's
     `cardHasCta` applies: the container books a click on any TWO of
     placement, name and destination, so the AC11 `<button>` form — placement
     plus its aria-label name, no destination — would report a CTA that goes
     nowhere, as would the "#" above. Spread rather than passed flat because
     `ButtonAnalyticsProps` types `linkAttributes` as the literal `true`, so there
     is no boolean to pass. */
  const ctaReporting = ctaUrl !== "" && ctaUrl !== "#" ?
    {
      linkAttributes: true as const,
      linkPosition: LINK_POSITION_BODY
    } :
    {};
  /* Closed sets, checked rather than cast (AC2, WAT-12200). Button has a
     guard of its own, but its fallbacks are `large` and `primary` where the
     frame draws `small` — so passing an off-set value through resolved the
     CTA one size UP instead of leaving the frame's own. `""` for a refusal,
     which is the same path an unauthored field takes. */
  const authoredCtaSize = text(card.cta_size);
  const authoredCtaVariant = text(card.cta_variant);
  const ctaSizeIsPublished = Object.prototype.hasOwnProperty.call(
    BUTTON_SIZES,
    authoredCtaSize
  );
  const ctaVariantIsPublished = Object.prototype.hasOwnProperty.call(
    BUTTON_VARIANTS,
    authoredCtaVariant
  );
  const ctaSize = (ctaSizeIsPublished ? authoredCtaSize : "") as ButtonSize | "";
  const ctaVariant = (ctaVariantIsPublished ? authoredCtaVariant : "") as ButtonVariant | "";
  /* `text()`, not `content()`: a destination is plumbing, never customer-visible
     copy. A refusal is reported as a REFUSED DESTINATION below rather than as
     suppressed copy, which would name the wrong field. */
  const authoredCompareUrl = text(card.compare_url);
  const compareUrl = authoredCompareUrl === "" ? "" : safeHref(authoredCompareUrl);
  /* ONE boolean for the element AND the reporting. ⚠ Asymmetric against `cta_url`,
     where AC11 keeps the `<button>` either way: a compare control leading nowhere
     announces a link and does nothing. design-conflicts/grow-card-carousel.mdx. */
  const compareIsLink = compareUrl !== "" && compareUrl !== "#";
  /* Through `content()` like `cta_aria_label`: an accessible name IS customer-
     facing copy. Read ONLY where the link renders — on the plain run the field
     reaches no DOM, so naming it would report a suppression nothing shows. */
  const compareName = compareIsLink && compareLabel !== "" ?
    ctaAriaLabel(content(card.compare_aria_label, "compare_aria_label", unresolved)) :
    undefined;
  /* Read EXACTLY as the CTA's two above, and for the same reason: Button's own
     fallbacks are `large` and `primary` where this chip draws `small` and
     `tonal`, so an off-set value passed through would resolve a different
     control rather than leaving the frame's. `""` for a refusal, which is the
     path an unauthored field takes. ⚠ `text()`, not `content()`: a published
     keyword is plumbing, never customer-visible copy. */
  const authoredCompareSize = text(card.compare_size);
  const authoredCompareVariant = text(card.compare_variant);
  const compareSizeIsPublished = Object.prototype.hasOwnProperty.call(
    BUTTON_SIZES,
    authoredCompareSize
  );
  const compareVariantIsPublished = Object.prototype.hasOwnProperty.call(
    BUTTON_VARIANTS,
    authoredCompareVariant
  );
  const compareSize = (compareSizeIsPublished ? authoredCompareSize : "") as ButtonSize | "";
  const compareVariant = (compareVariantIsPublished ? authoredCompareVariant : "") as
    ButtonVariant | "";
  /* An Asset field, so safeAssetUrl() and not text(): the same field is
     authored as a Storyblok Asset on some feeds and as a plain URL string on
     others, and the scheme gate is that reader's own. */
  const imageDesktopUrl = safeAssetUrl(card.image_desktop_url);
  /* Through content(), not text(): an unresolved [<TOKEN>] in an `alt` is read
     out as the accessible name and shown as the broken-image label (AC8). */
  const imageAlt = content(card.image_alt, "image_alt", unresolved);

  /* Both sizes: the ungrown frames draw the same wrapping channel row as the
     grown one (AC35). */
  const channelLogos = selectRenderableLogos(card.channel_logos, "channel_logos", unresolved);
  const benefits = grown ? selectRenderableBenefits(card.benefits, unresolved) : [];
  const svods = selectRenderableLogos(card.svods, "svods", unresolved);

  /* The HEADING steps, not the whole ramp: the title defaults to h3 and its two
     frame defaults are heading steps, so the body set would refuse one of its
     own defaults (WAT-12200). The boolean stays beside `safeVariant` below
     because it carries what `safeVariant` cannot — whether `cardTitleFrame`
     and the development diagnostic apply. */
  const titleAs = safeElement(text(card.title_level), "h3", TEXT_LEVELS);
  const subheadingStyle = safeVariant(text(card.subheading_variant), "body-3", TEXT_BODY_VARIANTS);
  const subheadingAs = safeElement(text(card.subheading_element), "span", TEXT_BODY_ELEMENTS);
  const benefitsHeadingAs = safeElement(text(card.benefits_heading_level), "p", TEXT_LEVELS);
  const benefitsHeadingStyle = safeVariant(
    text(card.benefits_heading_variant),
    "body-3",
    TEXT_HEADING_VARIANTS
  );
  const authoredTitleVariant = text(card.title_variant);
  const titleIsHonoured = Object.prototype.hasOwnProperty.call(
    TEXT_HEADING_VARIANTS,
    authoredTitleVariant
  );

  /* One boolean per wrapper: a field's absence has to take its wrapper and its
     spacing with it. */
  const hasHead = title !== "" || subheading !== "";
  const hasPrice = dollars !== "";
  /* The two controls share one column, so it has to survive either of them
     alone and disappear with both. */
  const hasActions = ctaLabel !== "" || compareLabel !== "";
  const hasPriceRow = hasPrice || hasActions;
  const hasContent = hasHead || channelLogos.length > 0 || svods.length > 0;
  const hasBody = hasContent || imageDesktopUrl !== "" || hasPriceRow;
  /* GROWN only, heading included: the frame draws no "What you get" on the
     ungrown card. */
  const hasBenefits = grown && (benefitsHeading !== "" || benefits.length > 0);
  /* WAT-12079: a row whose every alt is blank announces as "list, N items" and
     then nothing — a stop in the reading order carrying no information (AC30).
     Read per row: either array can be authored, or resolved, entirely blank. */
  const svodsDecorative = svods.every(logo => logo.logo_alt === "");
  const channelLogosDecorative = channelLogos.every(logo => logo.logo_alt === "");

  if (process.env.NODE_ENV !== "production") {
    /* The runs that live inside the price block and so cannot outlive it. */
    const ridingThePrice: [string, string][] = [
      ["strikethrough_price", strikethroughPrice],
      ["price_cents", priceCents],
      ["price_frequency", frequency],
      ["legal_content", legalContent]
    ];
    const paints: [PaintField, unknown][] = [
      ["accent", card.accent],
      ["accent_deep", card.accent_deep],
      ["title_color", card.title_color]
    ];
    /* Whether this card publishes an accent OF ITS OWN — the same reader that
       sets `--gcc-accent` below. See the titleAA note for why paint(). */
    const paintsOwnAccent = paint(card.accent) !== undefined;

    warnInDevelopment({
      droppedByMissingPrice: hasPrice ?
        [] :
        ridingThePrice.filter(([, value]) => value !== "").map(([field]) => field),
      id: text(card.id),
      /* Only where the button is drawn — the ungrown card reads these two and
         renders no call to action, so a refusal there names nothing on screen. */
      refusedCta: ctaLabel === "" ?
        [] :
        selectRefusedButtonFields([
          ["cta_size", authoredCtaSize, ctaSizeIsPublished, "size, e.g. small or medium"],
          [
            "cta_variant",
            authoredCtaVariant,
            ctaVariantIsPublished,
            "variant, e.g. primary or secondary"
          ]
        ]),
      /* Gated on the LINKED form, not just the wording: the plain run takes no
         Button props at all, so a refusal there names nothing on screen. */
      refusedCompare: compareIsLink && compareLabel !== "" ?
        selectRefusedButtonFields([
          ["compare_size", authoredCompareSize, compareSizeIsPublished, "size, e.g. small or medium"],
          [
            "compare_variant",
            authoredCompareVariant,
            compareVariantIsPublished,
            "variant, e.g. tonal or secondary"
          ]
        ]) :
        [],
      /* Gated on the WORDING, like refusedCta above: with no `compare_label`
         nothing is drawn there, so a refusal names nothing on screen. */
      refusedCompareUrl:
        compareLabel !== "" && authoredCompareUrl !== "" && compareUrl === "#" ?
          authoredCompareUrl :
          "",
      refusedLogoLayout: selectRefusedLogoLayout(logoLayout),
      refusedPaints: paints
        .filter(([, value]) => text(value) !== "" && paint(value) === undefined)
        .map(([field]) => field),
      seen: warnedRef.current,
      /* ONLY THE WASH LEG is skipped where the card paints no accent of its
         OWN: `--gcc-accent` is then left unset and, registered
         `inherits: true`, resolves to the section's accent — the grown
         card's — so any wash ratio taken here would describe a backdrop this
         card never shows. The FILL leg survives, because CARD_FILL is painted
         either way and a title that fails AA against it fails on every card. A
         ring-refused accent arrives as "" and is indistinguishable from an
         unauthored or a malformed one, so this one condition covers all three
         without a new prop. ⚠ `paint()`, and it HAS to be paint(): it is the
         very reader that publishes `--gcc-accent` below, so the leg is dropped
         for exactly the accents this card shows none of. An `opaque()` gate
         read NARROWER than that — it refuses the 4- and 8-digit alpha hex
         paint() PUBLISHES, so a translucent accent went unrated rather than
         unpainted and a real AA failure on it went unreported. titleContrast
         composites the alpha itself now, through `accentWash()`. WAT-12200. */
      titleAA: titleContrast(card.title_color, card.accent, paintsOwnAccent),
      titleAAAgainstWash: paintsOwnAccent,
      titleVariant: authoredTitleVariant,
      unresolved
    });
  }

  /* Legal copy rides inside the price block so it keeps the frame's position
     under the amount — PriceBlock takes it as a node, so a link sits inline. */
  let legal: React.ReactNode;
  if (legalContent !== "") {
    legal = legalUrl === "" ?
      legalContent :
      (
        <Link contrast="high" href={legalUrl} linkPosition={linkPosition || LINK_POSITION_BODY} size="small">
          {legalContent}
        </Link>
      );
  }

  return (
    <article
      aria-current={active ? "true" : undefined}
      className={`${styles.card} ${grown ? styles.grown : styles.ungrown}`}
      style={
        {
          "--gcc-accent": paint(card.accent),
          "--gcc-accent-deep": paint(card.accent_deep),
          "--gcc-chl-align": logoAlign(logoLayout.channel_logo_align),
          "--gcc-chl-fit": logoFit(logoLayout.channel_logo_object_fit),
          "--gcc-chl-h-lg": logoHeight(logoLayout.channel_logo_max_height_wide),
          "--gcc-chl-h-md": logoHeight(logoLayout.channel_logo_max_height_desktop),
          "--gcc-chl-h-sm": logoHeight(logoLayout.channel_logo_max_height_tablet),
          "--gcc-chl-h-xs": logoHeight(logoLayout.channel_logo_max_height),
          "--gcc-title": paint(card.title_color)
        } as React.CSSProperties
      }
    >
      {/* Its own layer, so the shell's accent cross-fade repaints the gradient
          rather than the content. */}
      <div aria-hidden="true" className={styles.wash} />

      {tagLabel !== "" && (
        <div className={styles.tagBand}>
          <Tag>{tagLabel}</Tag>
        </div>
      )}

      {hasBody && (
        <div className={styles.body}>
          {hasContent && (
            <div className={styles.content}>
              {hasHead && (
                <header className={styles.head}>
                  {title !== "" && (
                    <Text
                      as={titleAs}
                      className={[styles.cardTitle, titleIsHonoured ? null : styles.cardTitleFrame]
                        .filter(Boolean)
                        .join(" ")}
                      variant={safeVariant<TextHeadingVariant>(
                        authoredTitleVariant,
                        grown ? "headline-3" : "subhead-2",
                        TEXT_HEADING_VARIANTS
                      )}
                    >
                      {title}
                    </Text>
                  )}

                  {subheading !== "" && (
                    subheadingUrl === "" ?
                      (
                        <Text as={subheadingAs} className={styles.subheading} variant={subheadingStyle} weight={subheadingStyle === "body-3" ? "medium" : undefined}>
                          {subheading}
                        </Text>
                      ) :
                      (
                        <Link
                          /* WAT-12401: `.subheading` shrink-wraps (shared with
                             the text branch above); `.subheadingLink` cancels
                             the Link atom's inline padding. Without the pair
                             the wording sat 8px right of the title. */
                          className={`${styles.subheading} ${styles.subheadingLink}`}
                          contrast="high"
                          endIcon
                          href={subheadingUrl}
                          linkAction={subheadingAction}
                          linkPosition={linkPosition || LINK_POSITION_BODY}
                          size="small"
                        >
                          {subheading}
                        </Link>
                      )
                  )}
                </header>
              )}

              {svods.length > 0 && (
                <ul aria-hidden={svodsDecorative || undefined} className={styles.svods}>
                  {svods.map((logo, index) => (
                    <li className={styles.svod} key={`${index}-${logo.logo_url}`}>
                      <img
                        alt={logo.logo_alt}
                        className={styles.svodImage}
                        loading="lazy"
                        src={logo.logo_url}
                      />
                    </li>
                  ))}
                </ul>
              )}

              {channelLogos.length > 0 && (
                /* One wrapping row at every entry count — neither the count
                   nor the number of lines is encoded. */
                <ul
                  aria-hidden={channelLogosDecorative || undefined}
                  className={styles.channelLogos}
                >
                  {channelLogos.map((logo, index) => (
                    <li className={styles.channelLogo} key={`${index}-${logo.logo_url}`}>
                      <img
                        alt={logo.logo_alt}
                        className={styles.channelLogoImage}
                        loading="lazy"
                        src={logo.logo_url}
                      />
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}

          {imageDesktopUrl !== "" && (
            <div className={styles.art}>
              <img alt={imageAlt} className={styles.artImage} loading="lazy" src={imageDesktopUrl} />
            </div>
          )}

          {hasPriceRow && (
            <div className={styles.priceRow}>
              {hasPrice && (
                <div className={styles.priceSlot}>
                  <PriceBlock
                    cents={cents || undefined}
                    dollars={dollars}
                    frequency={frequency || undefined}
                    frequencyPlacement="below"
                    legal={legal}
                    legalElement={safeElement(text(card.legal_element), "p", TEXT_BODY_ELEMENTS)}
                    legalVariant={safeVariant(text(card.legal_variant), "label", TEXT_BODY_VARIANTS)}
                    strikethroughPrice={strikethroughPrice || undefined}
                  />
                </div>
              )}

              {hasActions && (
                <div className={styles.priceActions}>
                  {ctaLabel !== "" && (
                    <Button
                      {...ctaReporting}
                      ariaLabel={ctaName}
                      href={ctaUrl || undefined}
                      size={ctaSize || "small"}
                      variant={ctaVariant || "primary"}
                    >
                      {CTA_LABEL}
                    </Button>
                  )}

                  {compareLabel !== "" && (
                    compareIsLink ?
                      (
                        /* `linkAttributes`, never `linkName`: the container
                           books the click off the attributes, and a push on
                           top of them would count it twice (button.tsx). */
                        <Button
                          ariaLabel={compareName}
                          href={compareUrl}
                          linkAttributes
                          linkPosition={LINK_POSITION_BODY}
                          size={compareSize || "small"}
                          variant={compareVariant || "tonal"}
                        >
                          {compareLabel}
                        </Button>
                      ) :
                      (
                        /* THE NO-DESTINATION FALLBACK, and presentational on
                           purpose: with no `compare_url` there is nowhere to
                           go. Not `aria-hidden` either — this is the only
                           place the affordance is named. */
                        <span className={styles.compareLabel}>{compareLabel}</span>
                      )
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {hasBenefits && (
        <div className={styles.benefits}>
          {benefitsHeading !== "" && (
            <Text
              as={benefitsHeadingAs}
              className={benefitsHeadingStyle === "body-3" ? styles.benefitsHeadingType : undefined}
              variant={benefitsHeadingStyle}
              weight={benefitsHeadingStyle === "body-3" ? "bold" : undefined}
            >
              {benefitsHeading}
            </Text>
          )}

          {benefits.length > 0 && (
            <ul className={styles.benefitList}>
              {benefits.map((benefit, index) => (
                <li className={styles.benefit} key={`${index}-${benefit.content}`}>
                  {benefit.icon_url !== "" && (
                    <img
                      alt=""
                      className={styles.benefitIcon}
                      loading="lazy"
                      src={benefit.icon_url}
                    />
                  )}
                  {/* Default tone, slate-50 — NOT `secondary` (slate-300 is
                      under the Lc 75 body floor). */}
                  <Text
                    as={benefit.element}
                    className={benefit.variant === "body-3" ? styles.benefitTextType : undefined}
                    variant={benefit.variant}
                  >
                    {benefit.content}
                  </Text>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

    </article>
  );
}

/* Memoised on measured evidence, not on principle: the rail holds fifteen
   slots and every one re-rendered on each card change; only the two cards
   whose size flips actually change. ⚠ Shallow props comparison, so the shell
   must pass STABLE `card` objects. */
const GrowCardImpl = React.memo(GrowCardBase);
export const GrowCard = withSafeRender(GrowCardImpl, "GrowCard");

export default GrowCard;
